import * as def from '@/definitions'
import { ObjectLayer } from './objectLayer';
import { ColorConstruct } from '@/utilityModels/colorConstruct';
import { ImageHelper } from '@/helper/imageHelper';
import { MapHelper } from '@/helper/mapHelper';
import { Rectangle } from '@/utilityModels/rectangle';

// 画像レイヤー共通
export class ImageLayer extends ObjectLayer
{
    public get layerType() : string { return "image"; }

    // 画像差し込みのURL
    public insertionContent: string = "";
    // 差し込みフラグ
    public isInsertion : boolean = false;

    // 画像データ(byte[]が扱えないので、Base64文字列で扱う)
    private _imageBase64 : string = "";
    public get imageBase64() : string { return this._imageBase64; }
    public set imageBase64(imageBase64 : string) { this._imageBase64 = imageBase64; }

    // 白色透明画像データ
    private _imageBase64WhiteTransparent : string = "";
    public get imageBase64WhiteTransparent() { return this._imageBase64WhiteTransparent; }
    public set imageBase64WhiteTransparent(imageBase64WhiteTransparent) { this._imageBase64WhiteTransparent = imageBase64WhiteTransparent; }

    // オリジナル画像データ : 画像加工時に元画像を保持しておくため
    private _imageBase64Origin : string = "";
    public get imageBase64Origin() { return this._imageBase64Origin; }
    public set imageBase64Origin(imageBase64Origin) { this._imageBase64Origin = imageBase64Origin; }

    // オリジナル白色画像データ　: 画像加工時に元画像を保持しておくため
    private _imageBase64WhiteTransparentOrigin : string = "";
    public get imageBase64WhiteTransparentOrigin() { return this._imageBase64WhiteTransparentOrigin; }
    public set imageBase64WhiteTransparentOrigin(imageBase64WhiteTransparentOrigin) { this._imageBase64WhiteTransparentOrigin = imageBase64WhiteTransparentOrigin; }

    // 白色透明化
    private _whiteTransparent : boolean = false;
    public get whiteTransparent() : boolean { return this._whiteTransparent; }
    public set whiteTransparent(whiteTransparent : boolean) { this._whiteTransparent = whiteTransparent; }

    // 不透明度(%)
    private _opacity : number = 100;
    public get opacity() : number { return this._opacity; }
    public set opacity(opacity : number) { this._opacity = opacity; }

    // 色調整
    private _colorFilterR : number = 0;
    public get colorFilterR() { return this._colorFilterR; }
    public set colorFilterR(colorFilterR) { this._colorFilterR = colorFilterR; }
    private _colorFilterG : number = 0;
    public get colorFilterG() { return this._colorFilterG; }
    public set colorFilterG(colorFilterG) { this._colorFilterG = colorFilterG; }
    private _colorFilterB : number = 0;
    public get colorFilterB() { return this._colorFilterB; }
    public set colorFilterB(colorFilterB) { this._colorFilterB = colorFilterB; }

    // ぼかし
    private _blur : number = 0;
    public get blur() : number { return this._blur; }
    public set blur(blur : number) { this._blur = blur; }

    // 固定タイプ
    // 0: 縦横固定
    // 1; 縦固定
    // 2: 横固定
    private _keepRatio : number = 0;
    public get keepRatio() : number
    {
        return this._keepRatio;
    }
    public set keepRatio(keepRatio : number)
    {
        this._keepRatio = keepRatio;
    }

    // 左右反転
    private _flipHorizontal : boolean = false;
    public get flipHorizontal() : boolean
    {
        return this._flipHorizontal;
    }
    public set flipHorizontal(flipHorizontal : boolean)
    {
        this._flipHorizontal = flipHorizontal;
    }

    // 上下反転
    private _flipVertical : boolean = false;
    public get flipVertical() : boolean
    {
        return this._flipVertical;
    }
    public set flipVertical(flipVertical : boolean)
    {
        this._flipVertical = flipVertical;
    }

    // 影表示フラグ
    public isShadow : boolean = false;
    // 影色
    public shadowColor : ColorConstruct = new ColorConstruct(102, 102, 102);
    // 影距離
    public shadowDistance : number = 4;
    // 影角度
    public shadowRotate : number = 45;
    // 影ぼかし
    public shadowBlur : number = 5;
    // 影強さ
    public shadowIntensity : number = 100;

    // トリミング有効
    private _isTrim : boolean = false;
    public get isTrim() { return this._isTrim; }
    public set isTrim(isTrim) { this._isTrim = isTrim; }

    // トリミング領域(各辺100で正規化)
    private _trimRectNormalized : Rectangle = new Rectangle(0, 0, 100, 100);
    public get trimRectNormalized() { return this._trimRectNormalized; }
    public set trimRectNormalized(trimRectNormalized) { this._trimRectNormalized = trimRectNormalized; }

    // クリッピング有効
    private _isClip : boolean = false;
    public get isClip() { return this._isClip; }
    public set isClip(isClip) { this._isClip = isClip; }

    // クリッピング形状(svg path)
    private _clipShapeSvgPath : string = "";
    public get clipShapeSvgPath() { return this._clipShapeSvgPath; }
    public set clipShapeSvgPath(clipShapeSvgPath) { this._clipShapeSvgPath = clipShapeSvgPath; }

    // 元画像サイズ
    private _imageWidth : number = 1;
    public get imageWidth() { return this._imageWidth; }
    public set imageWidth(imageWidth) { this._imageWidth = imageWidth; }
    private _imageHeight: number = 1;
    public get imageHeight() { return this._imageHeight; }
    public set imageHeight(imageHeight) { this._imageHeight = imageHeight; }

    // 白色透明画像セット
    public setWhiteTransparentImage(imageWhiteTransparent : string)
    {
        this.imageBase64WhiteTransparent = imageWhiteTransparent;
        this.imageBase64WhiteTransparentOrigin = imageWhiteTransparent;
    }

    // サムネイル画像
    public thumbnailImage : string = "";
    public thumbnailImageWhiteTransparent : string = "";

    public clone()
    {
        var cloned = new ImageLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : ImageLayer)
    {
        layer.imageBase64 = this._imageBase64;
        layer.imageBase64WhiteTransparent = this.imageBase64WhiteTransparent;
        layer.imageBase64Origin = this.imageBase64Origin;
        layer.imageBase64WhiteTransparentOrigin = this.imageBase64WhiteTransparentOrigin;
        layer.whiteTransparent = this._whiteTransparent;
        layer.opacity = this._opacity;
        layer.colorFilterR = this._colorFilterR;
        layer.colorFilterG = this._colorFilterG;
        layer.colorFilterB = this._colorFilterB;
        layer.blur = this._blur;
        layer.keepRatio = this._keepRatio;
        layer.flipHorizontal = this._flipHorizontal;
        layer.flipVertical = this._flipVertical;
        layer.isShadow = this.isShadow;
        layer.shadowColor = this.shadowColor.clone();
        layer.shadowDistance = this.shadowDistance;
        layer.shadowRotate = this.shadowRotate;
        layer.shadowBlur = this.shadowBlur;
        layer.shadowIntensity = this.shadowIntensity;
        layer.isTrim = this._isTrim;
        layer.trimRectNormalized = this._trimRectNormalized.clone();
        layer.isClip = this._isClip;
        layer.clipShapeSvgPath = this._clipShapeSvgPath;
        layer.imageWidth = this._imageWidth;
        layer.imageHeight = this._imageHeight;

        layer.thumbnailImage = this.thumbnailImage;
        layer.thumbnailImageWhiteTransparent = this.thumbnailImageWhiteTransparent;

        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        // イメージはドキュメントに保存する
        // jsonObject.imageBase64 = this._imageBase64;
        // jsonObject.imageBase64WhiteTransparent = this._imageBase64WhiteTransparent;
        jsonObject.whiteTransparent = this._whiteTransparent;
        jsonObject.opacity = this._opacity;
        jsonObject.colorFilterR = this._colorFilterR;
        jsonObject.colorFilterG = this._colorFilterG;
        jsonObject.colorFilterB = this._colorFilterB;
        jsonObject.blur = this._blur;
        jsonObject.keepRatio = this._keepRatio;
        jsonObject.flipHorizontal = this._flipHorizontal;
        jsonObject.flipVertical = this._flipVertical;
        jsonObject.isShadow = this.isShadow;
        jsonObject.shadowColor = this.shadowColor;
        jsonObject.shadowDistance = this.shadowDistance;
        jsonObject.shadowRotate = this.shadowRotate;
        jsonObject.shadowBlur = this.shadowBlur;
        jsonObject.shadowIntensity = this.shadowIntensity;
        jsonObject.isTrim = this._isTrim;
        jsonObject.trimRectNormalized = this._trimRectNormalized;
        jsonObject.isClip = this._isClip;
        jsonObject.clipShapeSvgPath = this._clipShapeSvgPath;
        jsonObject.imageWidth = this._imageWidth;
        jsonObject.imageHeight = this._imageHeight;

        // jsonObject.thumbnailImage = this.thumbnailImage;
        // jsonObject.thumbnailImageWhiteTransparent = this.thumbnailImageWhiteTransparent;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : ImageLayer
    {
        var layer = new ImageLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        // イメージが保存ファイルに含まれている場合はここで復帰する
        if(jsonData.imageBase64 != null) { this._imageBase64 = jsonData.imageBase64; }
        if(jsonData.imageBase64WhiteTransparent != null) { this._imageBase64WhiteTransparent = jsonData.imageBase64WhiteTransparent; }
        if(jsonData.imageBase64Origin != null) { this._imageBase64Origin = jsonData.imageBase64Origin; }
        if(jsonData.imageBase64WhiteTransparentOrigin != null) { this._imageBase64WhiteTransparentOrigin = jsonData.imageBase64WhiteTransparentOrigin; }

        if(jsonData.whiteTransparent != null) { this._whiteTransparent = jsonData.whiteTransparent; }
        if(jsonData.opacity != null) { this._opacity = jsonData.opacity; }
        if(jsonData.colorFilterR != null) { this._colorFilterR = jsonData.colorFilterR; }
        if(jsonData.colorFilterG != null) { this._colorFilterG = jsonData.colorFilterG; }
        if(jsonData.colorFilterB != null) { this._colorFilterB = jsonData.colorFilterB; }
        if(jsonData.blur != null) { this._blur = jsonData.blur; }
        if(jsonData.keepRatio != null) { this._keepRatio = jsonData.keepRatio; }
        if(jsonData.flipHorizontal != null) { this._flipHorizontal = jsonData.flipHorizontal; }
        if(jsonData.flipVertical != null) { this._flipVertical = jsonData.flipVertical;}
        if(jsonData.isShadow != null) { this.isShadow = jsonData.isShadow; }
        if(jsonData.shadowColor != null) { this.shadowColor = ColorConstruct.createFromJsonData(jsonData.shadowColor); }
        if(jsonData.shadowRotate != null) { this.shadowRotate = jsonData.shadowRotate; }
        if(jsonData.shadowDistance != null) { this.shadowDistance = jsonData.shadowDistance; }
        if(jsonData.shadowBlur != null) { this.shadowBlur = jsonData.shadowBlur; }
        if(jsonData.shadowIntensity != null) { this.shadowIntensity = jsonData.shadowIntensity; }
        if(jsonData.isTrim != null) { this._isTrim = jsonData.isTrim; }
        if(jsonData.trimRectNormalized != null)
        {
            var trimRect = Rectangle.createFromJsonData(jsonData.trimRectNormalized);
            // 異常な値のチェック *誤差を許容する
            if(trimRect.x < 0 || trimRect.y < 0 || trimRect.x + trimRect.width > 100.01 || trimRect.y + trimRect.height > 100.01)
            {
                trimRect = new Rectangle(0, 0, 100, 100);
            }
            this._trimRectNormalized = trimRect;
        }
        if(jsonData.isClip != null) { this._isClip = jsonData.isClip;}
        if(jsonData.clipShapeSvgPath != null) { this._clipShapeSvgPath = jsonData.clipShapeSvgPath; }
        if(jsonData.imageWidth != null) { this._imageWidth = jsonData.imageWidth; }
        if(jsonData.imageHeight != null) { this._imageHeight = jsonData.imageHeight; }

        // if(jsonData.thumbnailImage != null) { this.thumbnailImage = jsonData.thumbnailImage; }
        // if(jsonData.thumbnailImageWhiteTransparent != null) { this.thumbnailImageWhiteTransparent = jsonData.thumbnailImageWhiteTransparent; }

        super.loadParameterFromJson(jsonData);
    }
}
